
import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X, Eye, Trash2, Volume2, VolumeX } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Avatar } from "@/components/ui/avatar";

interface StoryViewer {
  viewer_id: string;
  profiles: {
    username: string;
    avatar_url: string | null;
  };
}

interface StoryViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storyUrl: string;
  mediaType: string;
  isOwnStory?: boolean;
  viewCount?: number;
  storyId: string;
  onComplete?: () => void;
  onPrevious?: () => void;
  totalStories: number;
  currentStoryIndex: number;
  textOverlay?: string | null;
  audioUrl?: string | null;
  audioStartTime?: number | null;
  username?: string;
  audioTitle?: string | null;
  audioArtist?: string | null;
}

export const StoryViewer = ({ 
  open, 
  onOpenChange, 
  storyUrl, 
  mediaType = '',
  isOwnStory = false,
  viewCount = 0,
  storyId,
  onComplete,
  onPrevious,
  totalStories,
  currentStoryIndex,
  textOverlay,
  audioUrl,
  audioStartTime = 0,
  username,
  audioTitle,
  audioArtist
}: StoryViewerProps) => {
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [actualViewCount, setActualViewCount] = useState(viewCount);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: viewers = [], isLoading: viewersLoading } = useQuery({
    queryKey: ['story-viewers', storyId],
    queryFn: async () => {
      console.log('Fetching viewers for story:', storyId);
      const { data, error } = await supabase
        .from('story_views')
        .select(`
          viewer_id,
          profiles:viewer_id (
            username,
            avatar_url
          )
        `)
        .eq('story_id', storyId)
        .order('viewed_at', { ascending: false });

      if (error) {
        console.error('Error fetching viewers:', error);
        throw error;
      }

      // Update the actual view count based on the fetched data
      if (data) {
        setActualViewCount(data.length);
      }

      console.log('Fetched viewers:', data);
      return (data as unknown as StoryViewer[]) || [];
    },
    enabled: open && isOwnStory && !!storyId,
  });

  // Fetch the initial view count when the component mounts or storyId changes
  useEffect(() => {
    if (open && isOwnStory && storyId) {
      const fetchViewCount = async () => {
        const { count, error } = await supabase
          .from('story_views')
          .select('*', { count: 'exact', head: true })
          .eq('story_id', storyId);
        
        if (error) {
          console.error('Error fetching view count:', error);
          return;
        }
        
        if (count !== null) {
          setActualViewCount(count);
        }
      };
      
      fetchViewCount();
    }
  }, [open, isOwnStory, storyId]);

  useEffect(() => {
    if (open && !isPaused) {
      setProgress(0);
      if (audioRef.current && audioUrl) {
        audioRef.current.currentTime = audioStartTime || 0;
        audioRef.current.muted = isMuted;
        audioRef.current.play().catch(err => {
          console.error("Error playing audio:", err);
        });
      }
      
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            if (audioRef.current) {
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
            }
            onComplete?.();
            return 0;
          }
          return prev + (100 / 15 / 10); // Increment for smooth 15-second progress
        });
      }, 100);

      return () => {
        clearInterval(interval);
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
      };
    }
  }, [open, onOpenChange, isPaused, onComplete, audioUrl, audioStartTime, isMuted]);

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('stories')
        .delete()
        .eq('id', storyId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Story deleted successfully",
      });

      queryClient.invalidateQueries({ queryKey: ["stories"] });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete story",
        variant: "destructive",
      });
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleNavigate = (e: React.MouseEvent) => {
    const { clientX } = e;
    const { left, width } = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const clickPosition = (clientX - left) / width;

    if (clickPosition > 0.5) {
      onComplete?.();
    } else {
      onPrevious?.();
    }
  };

  const renderProgressBars = () => {
    return Array.from({ length: totalStories }).map((_, index) => (
      <div key={index} className="flex-1 px-0.5">
        <Progress 
          value={index === currentStoryIndex ? progress : index < currentStoryIndex ? 100 : 0}
          className="h-1 bg-black/50"
        />
      </div>
    ));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[90vh] p-0 bg-black/90">
        <div className="absolute top-0 left-0 right-0 z-50">
          <div className="flex gap-1 p-2">
            {renderProgressBars()}
          </div>
          
          {(username || (audioTitle && audioArtist)) && (
            <div className="px-4 py-2 bg-black/50">
              {username && (
                <p className="text-white text-sm font-medium">@{username}</p>
              )}
              {audioTitle && audioArtist && (
                <p className="text-gray-300 text-xs">
                  {audioTitle} - {audioArtist}
                </p>
              )}
            </div>
          )}
        </div>
        
        <button
          onClick={() => onOpenChange(false)}
          className="absolute top-2 right-2 z-50 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
        >
          <X className="h-4 w-4 text-white" />
        </button>
        
        <div 
          className="w-full h-full flex items-center justify-center relative cursor-pointer"
          onClick={handleNavigate}
        >
          {mediaType?.startsWith('image/') ? (
            <img
              src={storyUrl}
              alt="Story"
              className="max-h-full w-full h-full object-contain"
            />
          ) : mediaType?.startsWith('video/') ? (
            <video
              src={storyUrl}
              controls={false}
              autoPlay
              className="max-h-full w-full h-full object-contain"
              playsInline
              muted
            />
          ) : (
            <div className="text-white">Unsupported media type</div>
          )}

          {textOverlay && (
            <div className="absolute bottom-20 left-4 right-4 p-2 bg-black/50 text-white rounded">
              {textOverlay}
            </div>
          )}

          {audioUrl && (
            <>
              <audio
                ref={audioRef}
                src={audioUrl}
                className="hidden"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMute();
                }}
                className="absolute bottom-20 right-4 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
              >
                {isMuted ? (
                  <VolumeX className="h-4 w-4 text-white" />
                ) : (
                  <Volume2 className="h-4 w-4 text-white" />
                )}
              </Button>
            </>
          )}

          {isOwnStory && (
            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4 px-4">
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="p-2 rounded-lg bg-black/50 hover:bg-black/70 transition-colors flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Eye className="h-4 w-4 text-white" />
                    <span className="text-sm text-white ml-1">{actualViewCount}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent 
                  className="w-48 p-2 bg-black/90 border-none text-white"
                  side="top"
                >
                  {viewersLoading ? (
                    <div className="text-center py-2">Loading...</div>
                  ) : viewers.length === 0 ? (
                    <div className="text-center text-sm text-gray-400">
                      No viewers yet
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {viewers.map((viewer) => (
                        <div key={viewer.viewer_id} className="flex items-center gap-2">
                          <Avatar>
                            <img 
                              src={viewer.profiles.avatar_url || '/placeholder.svg'} 
                              alt={viewer.profiles.username}
                              className="w-6 h-6 rounded-full"
                            />
                          </Avatar>
                          <span className="text-sm">{viewer.profiles.username}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </PopoverContent>
              </Popover>

              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete();
                }}
                className="p-2 rounded-lg bg-black/50 hover:bg-red-500/70 transition-colors"
              >
                <Trash2 className="h-4 w-4 text-white" />
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
